import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Container, Grid, alpha } from "@mui/material";
import backphoto from "../img/terre_front_up.jpg";
import heroVideo from "../img/hero-video-transparent.webp";
import { Phone, MessageSquare, ChevronDown } from "lucide-react";
import StorefrontIcon from "@mui/icons-material/Storefront";

const rotatingMessages = [
  "Structurer les exploitations agricoles",
  "Piloter avec des données fiables",
  "Connecter producteurs, diaspora et investisseurs",
];

const floatingBadges = [
  { label: "Diagnostic", accent: "#7c3aed", top: { xs: 24, md: 80 }, left: { xs: 16, md: 70 }, delay: "0s" },
  { label: "Suivi terrain", accent: "#f59e0b", top: { xs: 84, md: 160 }, right: { xs: 18, md: 80 }, delay: "1.4s" },
  { label: "Pilotage stratégique", accent: "#2563eb", bottom: { xs: 98, md: 120 }, left: { xs: 18, md: 90 }, delay: "2.1s" },
];

const HeroSection = () => {
  const [activeMessageIndex, setActiveMessageIndex] = useState(0);
  const [activeSlide, setActiveSlide] = useState(1); // 1 = La vidéo apparaît dès le début au chargement de la page

  // Rotation des messages clés
  useEffect(() => {
    const messageInterval = window.setInterval(() => {
      setActiveMessageIndex((prevIndex) => (prevIndex + 1) % rotatingMessages.length);
    }, 3800);

    return () => window.clearInterval(messageInterval);
  }, []);

  // Séquence d'arrière-plan : la vidéo apparaît en premier (14s), puis l'image de fond seule (9s)
  useEffect(() => {
    const duration = activeSlide === 1 ? 14000 : 9000;
    const timer = window.setTimeout(() => {
      setActiveSlide((prev) => (prev === 1 ? 0 : 1));
    }, duration);

    return () => window.clearTimeout(timer);
  }, [activeSlide]);

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#01180e",
      }}
    >
      <style>
        {`
          @keyframes heroPanZoom {
            0% { transform: scale(1.02) translate3d(0, 0, 0); }
            25% { transform: scale(1.07) translate3d(-1.2%, -0.8%, 0); }
            50% { transform: scale(1.11) translate3d(0.8%, -1.4%, 0); }
            75% { transform: scale(1.07) translate3d(1.1%, 0.8%, 0); }
            100% { transform: scale(1.02) translate3d(0, 0, 0); }
          }

          @keyframes heroFloat {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
            100% { transform: translateY(0px); }
          }

          @keyframes heroPulse {
            0% { transform: translateY(0px); opacity: 0.75; }
            50% { transform: translateY(8px); opacity: 1; }
            100% { transform: translateY(0px); opacity: 0.75; }
          }
        `}
      </style>

      {/* ======================================================== */}
      {/* ARRIÈRE-PLAN : L'image de fond reste TOUJOURS visible     */}
      {/* La vidéo apparaît en petit par-dessus en séquence        */}
      {/* ======================================================== */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          bgcolor: "#01160d",
          zIndex: 1,
        }}
      >
        {/* 1. L'IMAGE DE FOND : Toujours présente et active à 100% du temps */}
        <Box
          component="img"
          src={backphoto}
          alt="Agri Vision Gabon Terrain"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            animation: "heroPanZoom 22s ease-in-out infinite",
            zIndex: 1,
          }}
        />

        {/* 2. Voile sombre adaptatif */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: activeSlide === 1 
              ? { xs: alpha("#000", 0.50), md: alpha("#000", 0.56) } 
              : { xs: alpha("#000", 0.42), md: alpha("#000", 0.52) },
            transition: "background-color 1400ms ease-in-out",
            zIndex: 2,
          }}
        />

        {/* Lueur d'ambiance agricole subtile */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(900px circle at 20% 30%, ${alpha("#014527", 0.32)} 0%, transparent 55%),
                         radial-gradient(700px circle at 85% 60%, ${alpha("#2563eb", 0.16)} 0%, transparent 55%)`,
            zIndex: 3,
            pointerEvents: "none",
          }}
        />

        {/* 3. L'ANIMATION EN TRANSPARENCE : Le planteur et ses cultures intégrés directement dans le décor */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: activeSlide === 1 ? "translate(-50%, -50%) scale(1)" : "translate(-50%, -50%) scale(0.96)",
            width: { xs: "96%", sm: "88%", md: "800px", lg: "880px" },
            maxWidth: "920px",
            aspectRatio: "16/9",
            opacity: activeSlide === 1 ? 1 : 0,
            transition: "opacity 1400ms ease-in-out, transform 1400ms ease-in-out",
            zIndex: 4,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={heroVideo}
            alt="Planteur Agri Vision Gabon"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
              filter: "drop-shadow(0 20px 35px rgba(0, 0, 0, 0.85))",
            }}
          />
        </Box>
      </Box>

      {/* Indicateurs discrets de séquence (Photo / Vidéo) - Masqué sur mobile */}
      <Box
        sx={{
          position: "absolute",
          bottom: 26,
          right: 32,
          zIndex: 10,
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          gap: 0.8,
          bgcolor: alpha("#000", 0.45),
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(255, 255, 255, 0.15)",
          borderRadius: 999,
          p: 0.5,
        }}
      >
        <Box
          component="button"
          onClick={() => setActiveSlide(0)}
          sx={{
            cursor: "pointer",
            border: "none",
            bgcolor: activeSlide === 0 ? "#10b981" : "transparent",
            color: activeSlide === 0 ? "#012817" : alpha("#fff", 0.8),
            fontWeight: 700,
            fontSize: "0.74rem",
            px: 1.4,
            py: 0.45,
            borderRadius: 999,
            transition: "all 0.3s ease",
            "&:hover": {
              color: "#fff",
            },
          }}
        >
          Photo
        </Box>
        <Box
          component="button"
          onClick={() => setActiveSlide(1)}
          sx={{
            cursor: "pointer",
            border: "none",
            bgcolor: activeSlide === 1 ? "#10b981" : "transparent",
            color: activeSlide === 1 ? "#012817" : alpha("#fff", 0.8),
            fontWeight: 700,
            fontSize: "0.74rem",
            px: 1.4,
            py: 0.45,
            borderRadius: 999,
            transition: "all 0.3s ease",
            "&:hover": {
              color: "#fff",
            },
          }}
        >
          Vidéo
        </Box>
      </Box>

      {/* Badges Flottants interactifs (Desktop) */}
      {floatingBadges.map((badge) => (
        <Box
          key={badge.label}
          onClick={() => {
            const section = document.getElementById("diagnostic");
            if (section) section.scrollIntoView({ behavior: "smooth" });
          }}
          sx={{
            position: "absolute",
            zIndex: 10,
            display: { xs: "none", md: "inline-flex" },
            alignItems: "center",
            px: 2,
            py: 1,
            borderRadius: 999,
            color: "white",
            bgcolor: alpha(badge.accent, 0.22),
            border: `1px solid ${alpha(badge.accent, 0.55)}`,
            backdropFilter: "blur(10px)",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
            fontSize: "0.92rem",
            fontWeight: 700,
            letterSpacing: 0.3,
            animation: `heroFloat 6s ease-in-out infinite`,
            animationDelay: badge.delay,
            cursor: "pointer",
            transition: "all 0.3s ease",
            "&:hover": {
              bgcolor: alpha(badge.accent, 0.45),
              boxShadow: `0 12px 35px ${alpha(badge.accent, 0.45)}`,
              transform: "translateY(-2px)",
            },
            ...badge,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: 999,
              bgcolor: badge.accent,
              boxShadow: `0 0 0 4px ${alpha(badge.accent, 0.2)}`,
              mr: 1.1,
            }}
          />
          {badge.label}
        </Box>
      ))}

      {/* ======================================================== */}
      {/* PREMIER PLAN : Contenu textuel centré et boutons d'action */}
      {/* ======================================================== */}
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 4, py: { xs: 5, md: 8 } }}>
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={10} lg={9}>
            <Box
              sx={{
                textAlign: "center",
                p: { xs: 2.5, sm: 3.5, md: 4.5 },
                borderRadius: "24px",
                backgroundColor: { xs: alpha("#000", 0.22), md: alpha("#000", 0.28) },
                backdropFilter: activeSlide === 0 ? "blur(8px)" : "none",
                border: "1px solid rgba(255, 255, 255, 0.12)",
                boxShadow: "0 12px 40px rgba(0, 0, 0, 0.35)",
                transition: "backdrop-filter 0.5s ease",
              }}
            >
              {/* Badge supérieur */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                  px: 2,
                  py: 0.6,
                  borderRadius: 999,
                  bgcolor: alpha("#014527", 0.85),
                  color: "#d1fae5",
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  fontSize: { xs: "0.78rem", md: "0.85rem" },
                  border: "1px solid rgba(16, 185, 129, 0.35)",
                  boxShadow: "0 4px 16px rgba(1, 69, 39, 0.3)",
                }}
              >
                🌱 AVG Consulting • Terrain & Données
              </Box>

              {/* Titre Principal */}
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 900,
                  mb: 1.5,
                  color: "white",
                  textTransform: "uppercase",
                  letterSpacing: { xs: "-0.5px", md: "0.8px" },
                  fontSize: { xs: "2.3rem", sm: "3.2rem", md: "3.75rem" },
                  lineHeight: 1.15,
                  textShadow: "0 3px 12px rgba(0,0,0,0.6)",
                }}
              >
                Agri Vision Gabon
              </Typography>

              {/* Sous-titre */}
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  lineHeight: 1.6,
                  color: "rgba(255, 255, 255, 0.92)",
                  maxWidth: "90%",
                  mx: "auto",
                  fontWeight: 300,
                  fontSize: { xs: "1.05rem", md: "1.35rem" },
                  textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                }}
              >
                Agri Vision Gabon s'engage en faveur de l'innovation et de la durabilité dans l'agriculture.
                Rejoignez-nous pour transformer l'avenir.
              </Typography>

              {/* Message rotatif */}
              <Box
                sx={{
                  minHeight: 34,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: { xs: 2, md: 3 },
                }}
              >
                <Typography
                  key={activeMessageIndex}
                  variant="subtitle1"
                  sx={{
                    color: "#fde68a",
                    fontWeight: 700,
                    fontSize: { xs: "0.92rem", md: "1.05rem" },
                    letterSpacing: 0.2,
                    animation: "heroFloat 4.5s ease-in-out infinite",
                    textShadow: "0 2px 8px rgba(0,0,0,0.6)",
                  }}
                >
                  ✨ {rotatingMessages[activeMessageIndex]}
                </Typography>
              </Box>

              {/* Badges pour mobile */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 2.5,
                }}
              >
                {floatingBadges.map((badge) => (
                  <Box
                    key={badge.label}
                    onClick={() => {
                      const section = document.getElementById("diagnostic");
                      if (section) section.scrollIntoView({ behavior: "smooth" });
                    }}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 1.5,
                      py: 0.6,
                      borderRadius: 999,
                      color: "white",
                      bgcolor: alpha(badge.accent, 0.28),
                      border: `1px solid ${alpha(badge.accent, 0.6)}`,
                      fontSize: "0.8rem",
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        bgcolor: badge.accent,
                        mr: 0.8,
                      }}
                    />
                    {badge.label}
                  </Box>
                ))}
              </Box>

              {/* Boutons d'action principaux */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "center",
                  alignItems: "center",
                  gap: { xs: 1.5, sm: 2 },
                  mt: 3.5,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<MessageSquare size={19} />}
                  sx={{
                    bgcolor: "#014527",
                    color: "white",
                    fontWeight: 700,
                    py: 1.4,
                    px: { xs: 3, sm: 3.5 },
                    borderRadius: "24px",
                    textTransform: "none",
                    fontSize: "0.98rem",
                    boxShadow: "0 4px 20px rgba(1, 69, 39, 0.55)",
                    "&:hover": {
                      bgcolor: "#01331d",
                      boxShadow: "0 6px 25px rgba(1, 69, 39, 0.7)",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.25s ease",
                    width: { xs: "100%", sm: "auto" },
                  }}
                  onClick={() => {
                    const diagnosticSection = document.getElementById("diagnostic");
                    if (diagnosticSection) {
                      diagnosticSection.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                >
                  Réserver un diagnostic
                </Button>

                <Button
                  disabled
                  variant="contained"
                  size="large"
                  startIcon={<StorefrontIcon sx={{ fontSize: "20px !important", color: "rgba(255, 255, 255, 0.5) !important" }} />}
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.14) !important",
                    color: "rgba(255, 255, 255, 0.6) !important",
                    border: "1px solid rgba(255, 255, 255, 0.22)",
                    fontWeight: 700,
                    py: 1.4,
                    px: { xs: 2.5, sm: 3 },
                    borderRadius: "24px",
                    textTransform: "none",
                    fontSize: "0.95rem",
                    boxShadow: "none !important",
                    cursor: "not-allowed !important",
                    pointerEvents: "auto !important",
                    width: { xs: "100%", sm: "auto" },
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.2
                  }}
                >
                  <span>Centrale d'Achat</span>
                  <Box
                    component="span"
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.22)",
                      color: "#ffffff",
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      px: 1,
                      py: 0.3,
                      borderRadius: "12px",
                      textTransform: "none",
                      letterSpacing: "0.3px",
                      lineHeight: 1
                    }}
                  >
                    Bientôt disponible
                  </Box>
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Phone size={19} />}
                  sx={{
                    color: "white",
                    borderColor: "rgba(255, 255, 255, 0.65)",
                    borderWidth: 1.5,
                    fontWeight: 600,
                    py: 1.4,
                    px: { xs: 2.5, sm: 3 },
                    borderRadius: "24px",
                    textTransform: "none",
                    fontSize: "0.95rem",
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: alpha("#ffffff", 0.12),
                    },
                    width: { xs: "100%", sm: "auto" },
                  }}
                  onClick={() => {
                    const phoneNumber = "+24104126488";
                    const message = encodeURIComponent("Bonjour Agri Vision Gabon, je souhaite avoir plus d'informations.");
                    window.open(`https://wa.me/${phoneNumber}?text=${message}`, "_blank");
                  }}
                >
                  Contact WhatsApp
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Indicateur de défilement vers le bas */}
      <Box
        sx={{
          position: "absolute",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          color: "white",
          opacity: 0.9,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            letterSpacing: 1.2,
            textTransform: "uppercase",
            mb: 0.6,
            fontSize: "0.72rem",
            color: alpha("#fff", 0.8),
          }}
        >
          Découvrir
        </Typography>
        <ChevronDown size={20} style={{ animation: "heroPulse 1.9s ease-in-out infinite" }} />
      </Box>
    </Box>
  );
};

export default HeroSection;
